import React, { useState } from 'react';
import { MapPin, Mail, MessageCircle, Clock, CheckCircle } from 'lucide-react';

const WHATSAPP_NUMBER = '233240551119'; // ← Replace with your real WhatsApp number e.g. 233244123456
const EMAIL_ADDRESS   = 'info@adelzone.com'; // ← Replace with your real email

const serviceOptions = [
  'Network Infrastructure & Connectivity',
  'Custom Software Architecture',
  'Cloud Computing & Remote Access',
  'Cybersecurity & Data Protection',
  'Business Intelligence & Analytics',
  'IoT & Industrial Connectivity',
  'IT Support & Managed Services',
  'Digital Transformation Consulting',
  'Technology Partnership',
  'Other / General Enquiry',
];

const contactItems = [
  { Icon: MapPin,        label: 'Location',      value: 'Accra, Ghana — Serving West Africa & Beyond' },
  { Icon: Mail,          label: 'Email',         value: EMAIL_ADDRESS },
  { Icon: MessageCircle, label: 'WhatsApp',      value: '+233 24 055 1119' },
  { Icon: Clock,         label: 'Response Time', value: 'Within 24 hours on business days' },
];

export default function Contact() {
  const [method,  setMethod]  = useState('whatsapp');
  const [sent,    setSent]    = useState(false);
  const [form,    setForm]    = useState({
    fname: '', lname: '', company: '', email: '', service: '', message: '',
  });

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSend = () => {
    if (!form.fname || !form.service || !form.message) {
      alert('Please fill in your name, service of interest, and message.');
      return;
    }
    const name = `${form.fname} ${form.lname}`.trim();
    const body = `*New Enquiry — ADELZONE Website*\n\n*Name:* ${name}\n*Company:* ${form.company || 'Not provided'}\n*Email:* ${form.email || 'Not provided'}\n*Service:* ${form.service}\n\n*Message:*\n${form.message}`;

    if (method === 'whatsapp') {
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(body)}`, '_blank');
    } else {
      const subject = encodeURIComponent(`Enquiry: ${form.service} — ${name}`);
      const emailBody = encodeURIComponent(body.replace(/\*/g, ''));
      window.open(`mailto:${EMAIL_ADDRESS}?subject=${subject}&body=${emailBody}`, '_blank');
    }
    setSent(true);
    setTimeout(() => setSent(false), 4000);
  };

  const inputStyle = {
    width: '100%',
    background: 'rgba(0,180,216,0.04)',
    border: '1px solid rgba(0,180,216,0.15)',
    borderRadius: '8px', padding: '0.75rem 1rem',
    color: 'var(--white)', fontSize: '0.9rem',
    outline: 'none', transition: 'border-color 0.2s',
  };

  const labelStyle = {
    fontSize: '0.76rem', fontWeight: 600,
    color: 'var(--slate-light)', letterSpacing: '0.3px',
    display: 'block', marginBottom: '0.4rem',
  };

  return (
    <section id="contact" style={{ padding: '100px 5%', background: 'var(--navy)' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '5rem', alignItems: 'start',
      }}>

        {/* Left — Info */}
        <div>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--electric)', marginBottom: '0.8rem' }}>
            Get in Touch
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.9rem,3.5vw,2.8rem)', fontWeight: 700, color: 'var(--white)', letterSpacing: '-0.8px', lineHeight: 1.15, marginBottom: '1rem' }}>
            Let's talk about your technology challenges
          </h2>
          <p style={{ color: 'var(--slate)', fontSize: '1rem', maxWidth: '420px', lineHeight: 1.7, marginBottom: '2.5rem' }}>
            Whether you're looking to modernise your infrastructure, build a custom system, or explore a technology partnership — we're ready to listen.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.4rem' }}>
            {contactItems.map((c, i) => (
              <div key={i} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{
                  width: 42, height: 42, borderRadius: '10px', flexShrink: 0,
                  background: 'rgba(0,180,216,0.1)',
                  border: '1px solid rgba(0,180,216,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--electric)',
                }}>
                  <c.Icon size={18}/>
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--electric)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                    {c.label}
                  </div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--white)' }}>{c.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — Form */}
        <div style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--card-border)',
          borderRadius: '16px', padding: '2.4rem',
        }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 600, color: 'var(--white)', marginBottom: '0.35rem' }}>
            Send us an enquiry
          </div>
          <div style={{ fontSize: '0.84rem', color: 'var(--slate)', marginBottom: '1.6rem' }}>
            Choose how you'd like to reach us — WhatsApp for quick chats, email for detailed enquiries.
          </div>

          {/* Method toggle */}
          <div style={{ display: 'flex', gap: '0.7rem', marginBottom: '1.5rem' }}>
            {[
              { key: 'whatsapp', label: '💬 WhatsApp', bg: '#25D366', color: '#fff' },
              { key: 'email',    label: '📧 Email',    bg: 'var(--electric)', color: 'var(--navy)' },
            ].map(m => (
              <button key={m.key}
                onClick={() => setMethod(m.key)}
                style={{
                  flex: 1, padding: '0.65rem 1rem', borderRadius: '8px',
                  background: method === m.key ? m.bg : 'rgba(255,255,255,0.04)',
                  color: method === m.key ? m.color : 'var(--slate-light)',
                  border: method === m.key ? 'none' : '1px solid rgba(255,255,255,0.1)',
                  fontWeight: 600, fontSize: '0.85rem',
                  transition: 'all 0.2s',
                  boxShadow: method === m.key ? '0 0 0 2px rgba(255,255,255,0.15)' : 'none',
                }}>
                {m.label}
              </button>
            ))}
          </div>

          {/* Name row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.9rem', marginBottom: '0.9rem' }}>
            {[['fname','First Name','John'], ['lname','Last Name','Mensah']].map(([name, label, ph]) => (
              <div key={name}>
                <label style={labelStyle}>{label}</label>
                <input name={name} value={form[name]} onChange={handleChange}
                  placeholder={ph} style={inputStyle}
                  onFocus={e => e.target.style.borderColor='var(--electric)'}
                  onBlur={e  => e.target.style.borderColor='rgba(0,180,216,0.15)'}/>
              </div>
            ))}
          </div>

          {/* Company */}
          <div style={{ marginBottom: '0.9rem' }}>
            <label style={labelStyle}>Company / Organisation</label>
            <input name="company" value={form.company} onChange={handleChange}
              placeholder="Your Company Ltd" style={inputStyle}
              onFocus={e => e.target.style.borderColor='var(--electric)'}
              onBlur={e  => e.target.style.borderColor='rgba(0,180,216,0.15)'}/>
          </div>

          {/* Email */}
          <div style={{ marginBottom: '0.9rem' }}>
            <label style={labelStyle}>Email Address</label>
            <input name="email" type="email" value={form.email} onChange={handleChange}
              placeholder="you@company.com" style={inputStyle}
              onFocus={e => e.target.style.borderColor='var(--electric)'}
              onBlur={e  => e.target.style.borderColor='rgba(0,180,216,0.15)'}/>
          </div>

          {/* Service */}
          <div style={{ marginBottom: '0.9rem' }}>
            <label style={labelStyle}>Service of Interest</label>
            <select name="service" value={form.service} onChange={handleChange}
              style={{ ...inputStyle, cursor: 'pointer', appearance: 'none' }}>
              <option value="">Select a service...</option>
              {serviceOptions.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          {/* Message */}
          <div style={{ marginBottom: '1.3rem' }}>
            <label style={labelStyle}>Your Message</label>
            <textarea name="message" value={form.message} onChange={handleChange}
              placeholder="Tell us about your project, challenges, or what you're looking to achieve..."
              rows={4}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '100px' }}
              onFocus={e => e.target.style.borderColor='var(--electric)'}
              onBlur={e  => e.target.style.borderColor='rgba(0,180,216,0.15)'}/>
          </div>

          {/* Send button */}
          <button onClick={handleSend} style={{
            width: '100%', padding: '0.9rem',
            borderRadius: '8px',
            background: sent ? '#22c55e' : 'var(--electric)',
            color: sent ? '#fff' : 'var(--navy)',
            fontWeight: 700, fontSize: '0.95rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
            transition: 'background 0.25s, transform 0.15s',
          }}
          onMouseEnter={e => !sent && (e.currentTarget.style.transform='translateY(-2px)')}
          onMouseLeave={e => (e.currentTarget.style.transform='translateY(0)')}>
            {sent ? <><CheckCircle size={18}/> Enquiry Sent!</> : <>Send Enquiry →</>}
          </button>

          <div style={{ fontSize: '0.74rem', color: 'var(--slate)', marginTop: '0.8rem', textAlign: 'center' }}>
            Your enquiry will be sent via {method === 'whatsapp' ? 'WhatsApp' : 'email'}. We respond within 24 hours.
          </div>
        </div>
      </div>
    </section>
  );
}
